
import React, { useState, useEffect } from 'react';
import { useSubscription } from '@/hooks/useSubscription';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, AlertCircle, Sparkles, Star, Clock, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuthSession } from '@/hooks/useAuthSession';
import { toast } from 'sonner';
import { useAdmin } from '@/hooks/useAdmin';
import { useIsMobile } from '@/hooks/use-mobile';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { formatCurrency, storePaymentBackup } from '@/utils/PaymentHelper';
import { assignFreePlan } from '@/utils/freePlanHelpers';
import { CheckoutButton } from './CheckoutButton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { motion } from 'framer-motion';
import { trackSubscriptionEvent } from '@/utils/subscriptionAnalytics';

interface SubscriptionPlansProps {
  mode?: 'public' | 'dashboard';
}

// Plan hierarchy for determining downgrades
const PLAN_RANKINGS = {
  'Free': 1,
  'Starter': 1, // Same as Free
  'Basic': 2,
  'Trial': 3,
  'Pro': 4
};

export function SubscriptionPlans({ mode = 'dashboard' }: SubscriptionPlansProps) {
  const [isAnnual, setIsAnnual] = useState(false);
  const [canUseTrial, setCanUseTrial] = useState(false);
  const { subscriptionPlans, subscription, refreshSubscription } = useSubscription();
  const { session } = useAuthSession();
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();
  
  const hasActiveSubscription = subscription?.has_active_subscription || false;
  const currentPlanName = subscription?.subscription?.plan;
  const discount = 25; // 25% discount for annual plans
  const isPublicMode = mode === 'public';
  const gstPercentage = 18; // 18% GST

  // Check if user is eligible for trial
  useEffect(() => {
    async function checkTrialEligibility() {
      if (!session?.user) return;
      
      try {
        const { data, error } = await supabase.rpc('has_used_trial', {
          p_user_id: session.user.id
        });
        
        if (error) {
          console.error('Error checking trial eligibility:', error);
          return;
        }
        
        // If data is false, user is eligible for trial
        setCanUseTrial(!data);
      } catch (err) {
        console.error('Error checking trial eligibility:', err);
      }
    }
    
    if (!isPublicMode) {
      checkTrialEligibility();
    }
  }, [session, isPublicMode]);

  const handleAuthRequired = () => {
    trackSubscriptionEvent('upgrade_clicked', { source_page: 'plans', requires_auth: true });
    navigate('/auth');
  };

  // Determine if a plan would be a downgrade from current plan
  const isDowngrade = (planName: string): boolean => {
    if (!currentPlanName || !hasActiveSubscription) return false;
    
    const currentRank = PLAN_RANKINGS[currentPlanName] || 0;
    const newRank = PLAN_RANKINGS[planName] || 0;
    
    return newRank < currentRank;
  };

  // Filter plans based on the billing cycle selection - only show Trial in monthly mode
  const filteredPlans = subscriptionPlans.filter(plan => {
    // Always hide trial plan for annual billing
    if (isAnnual && plan.is_trial) return false;
    
    // In dashboard mode:
    // - Hide trial if user is not eligible
    // - Show Free plan if user is on Free plan
    // - Hide Free plan if user has a paid subscription
    if (!isPublicMode) {
      if (plan.is_trial && !canUseTrial) return false;
      if (plan.name === 'Free' || plan.name === 'Starter') {
        // Show Free/Starter plan only if the user is currently on it,
        // or if they have no subscription
        if (currentPlanName === 'Free' || currentPlanName === 'Starter') return true;
        if (!hasActiveSubscription) return true;
        return false; // Hide for users with paid subscriptions
      }
    } else {
      // In public mode, always show Free plan but hide it in pricing lists
      if (isPublicMode && (plan.name === 'Free' || plan.name === 'Starter')) return true;
    }
    
    return true;
  });
  
  // Sort plans: Free/Starter first, then Trial, then by price
  const sortedPlans = [...filteredPlans].sort((a, b) => {
    // Free/Starter plan should always come first
    if ((a.name === 'Free' || a.name === 'Starter') && (b.name !== 'Free' && b.name !== 'Starter')) return -1;
    if ((a.name !== 'Free' && a.name !== 'Starter') && (b.name === 'Free' || b.name === 'Starter')) return 1;
    
    // Trial plans should come after Free/Starter plan but before others
    if (a.is_trial && !b.is_trial) return -1;
    if (!a.is_trial && b.is_trial) return 1;
    
    // Then sort by price
    const aPrice = isAnnual ? a.price_yearly : a.price_monthly;
    const bPrice = isAnnual ? b.price_yearly : b.price_monthly;
    return aPrice - bPrice;
  });

  // Determine grid columns based on number of visible plans, mode, and billing cycle
  const getGridColumns = () => {
    const visiblePlansCount = sortedPlans.length;
    
    if (isPublicMode) {
      if (isAnnual) {
        // For annual plans, always use 2 columns (no trial plan)
        return 'grid-cols-1 md:grid-cols-2';
      } else {
        // For monthly plans, use 3 columns if we have 3 or more plans
        return visiblePlansCount >= 3 ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2';
      }
    } else {
      // For dashboard mode, use grid that fits the available plans better
      if (visiblePlansCount === 1) {
        return 'grid-cols-1';
      } else if (visiblePlansCount === 2) {
        return 'grid-cols-1 md:grid-cols-2';
      } else {
        return 'grid-cols-1 md:grid-cols-3';
      }
    }
  };

  // Updated feature lists with correct naming and categorization
  const getFeaturesList = (planName: string) => {
    if (planName === 'Free' || planName === 'Starter') {
      return [
        'Adaptive Quiz Assessment',
        'Adaptive Study Notes', 
        'Adaptive Current Affairs',
        'Adaptive Flash Cards',
        '7-day access period'
      ];
    } else if (planName === 'Basic') {
      return [
        'Adaptive Quiz Assessment',
        'Advanced Analytics', // This is the key difference from Free plan
        'Adaptive Study Notes',
        'Adaptive Current Affairs',
        'Adaptive Flash Cards',
        'Full access to all flashcard sets',
        'Personalized study planner'
      ];
    } else if (planName === 'Pro') {
      return [
        'All Basic Plan Features',
        'Adaptive Learning Path',
        'Adaptive Video Recommendation',
        'Adaptive Mock Tests',
        'PYQ Multiplier',
        'AI Learning Studio',
        'Collaborative Learning',
        'Code Playground',
        'Advanced Analytics & Skill-gap Analysis',
        '24/7 AI Mentor Chatbot and Voice Tutor',
        'Advanced AI-driven personalization',
        'Exclusive forums & live Q&A sessions',
        'Advanced certificates',
        'Priority Support'
      ];
    } else if (planName === 'Trial') {
      return [
        'Adaptive Quiz Assessment',
        'Advanced Analytics',
        'Adaptive Study Notes',
        'Adaptive Current Affairs',
        'Adaptive Flash Cards',
        'Full access to all flashcard sets',
        'Adaptive Learning Path',
        'Adaptive Video Recommendation',
        'Adaptive Mock Tests',
        'PYQ Multiplier',
        'AI Learning Studio',
        'Collaborative Learning',
        'Code Playground',
        'Advanced Analytics & Skill-gap Analysis',
        '24/7 AI Mentor Chatbot and Voice Tutor',
        'Advanced AI-driven personalization',
        'Exclusive forums & live Q&A sessions',
        'Advanced certificates',
        'Priority Support'
      ];
    }
    
    // Default case - return plan's features if available or empty array
    return subscriptionPlans.find(p => p.name === planName)?.features || [];
  };

  // Dynamic pricing function using database values with GST calculation
  const getPriceDetails = (plan: any, isAnnual: boolean) => {
    if (plan.name === "Free" || plan.name === "Starter") {
      return {
        displayPrice: "Free",
        basePrice: 0,
        gstAmount: 0,
        total: 0
      };
    }
    
    // Get base price from database
    const basePrice = isAnnual ? plan.price_yearly : plan.price_monthly;
    const gstAmount = Math.round(basePrice * (gstPercentage / 100));
    const totalWithGst = basePrice + gstAmount;
    
    return {
      displayPrice: `₹${totalWithGst}`,
      basePrice: basePrice,
      gstAmount: gstAmount,
      total: totalWithGst
    };
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 }
  };

  // Check if the user is admin or has access approved
  const [isApproved, setIsApproved] = useState<boolean | null>(null);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  
  // Check if user is allowed access (admin or has approved_for_access)
  useEffect(() => {
    const checkAccess = async () => {
      if (session?.user?.email === 'admin@admin.com') {
        setIsApproved(true);
        setIsCheckingAccess(false);
        return;
      }
      
      if (isAdmin) {
        setIsApproved(true);
        setIsCheckingAccess(false);
        return;
      }
      
      if (!session?.user?.id) {
        setIsApproved(false);
        setIsCheckingAccess(false);
        return;
      }
      
      try {
        const { data: permissionsData, error } = await supabase.functions.invoke('check-auth-permissions');
        
        if (error) {
          console.error('Error checking permissions:', error);
          setIsApproved(false);
        } else {
          setIsApproved(permissionsData.is_allowed_access || permissionsData.is_admin);
        }
      } catch (err) {
        console.error('Error in permissions check:', err);
        setIsApproved(false);
      } finally {
        setIsCheckingAccess(false);
      }
    };
    
    checkAccess();
  }, [session, isAdmin]);

  const handleStartFreePlan = async (userId: string) => {
    if (!userId) {
      toast.error("Please sign in to start your free plan");
      navigate('/auth');
      return;
    }
    
    try {
      // Show loading toast
      toast.loading("Setting up your free plan...", { id: "free-plan-loading" });
      
      const success = await assignFreePlan(userId);
      
      if (success) {
        toast.dismiss("free-plan-loading");
        toast.success("Free plan activated successfully!", {
          description: "You now have 7 days of access to basic features."
        });
        
        // Track the event
        await trackSubscriptionEvent('plan_started', {
          plan_type: 'free',
          metadata: { source: 'subscription_page' }
        });
        
        // Refresh subscription data
        if (refreshSubscription) {
          await refreshSubscription();
        }
        
        // Redirect to dashboard
        navigate('/dashboard');
      } else {
        toast.dismiss("free-plan-loading");
        toast.error("Failed to activate free plan", {
          description: "Please try again or contact support."
        });
      }
    } catch (error) {
      console.error("Error starting free plan:", error);
      toast.dismiss("free-plan-loading");
      toast.error("An unexpected error occurred");
    }
  };

  return (
    <div className="flex flex-col items-center max-w-5xl mx-auto">
      {!isPublicMode && (
        <h2 className="text-2xl font-bold text-center mb-6 text-secondary-500">
          {hasActiveSubscription || isAdmin || isApproved
            ? 'You Have Access' 
            : 'No Active Subscription'}
        </h2>
      )}
      
      {(isAdmin || isApproved) && !isPublicMode && (
        <div className="bg-green-50 border border-green-200 p-4 rounded-md mb-6 w-full max-w-xl text-center">
          <p className="text-green-700">
            {isAdmin 
              ? "Admin access granted. You have access to all features." 
              : "Your account has been approved for access."}
          </p>
        </div>
      )}
      
      <div className="flex flex-col items-center justify-center gap-2 mb-6">
        <div className="flex items-center justify-center gap-3">
          <span className={`text-sm ${!isAnnual ? 'text-secondary-500 font-medium' : 'text-muted-foreground'}`}>
            Monthly
          </span>
          <Switch
            checked={isAnnual}
            onCheckedChange={setIsAnnual}
          />
          <span className={`text-sm ${isAnnual ? 'text-secondary-500 font-medium' : 'text-muted-foreground'}`}>
            Annual <span className="text-accent-500 font-medium">(Save {discount}%)</span>
          </span>
        </div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
          <p>*All prices include 18% GST</p>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3 w-3 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p>18% GST is included in all displayed prices</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      
      <motion.div 
        className={`grid gap-6 ${getGridColumns()} w-full justify-center`}
        variants={container}
        initial="hidden"
        animate="show"
      >
        {sortedPlans.map((plan) => {
          const isCurrentPlan = currentPlanName === plan.name || 
                               (currentPlanName === 'Free' && plan.name === 'Starter') || 
                               (currentPlanName === 'Starter' && plan.name === 'Free');
          const isFreePlan = plan.name === 'Free' || plan.name === 'Starter';
          const isProPlan = plan.name === 'Pro';
          const isBasicPlan = plan.name === 'Basic';
          const isTrial = plan.is_trial;
          const isPlanDowngrade = isDowngrade(plan.name);
          
          // Get dynamic pricing details from database
          const priceDetails = getPriceDetails(plan, isAnnual);
          // Get the updated features list
          const features = getFeaturesList(plan.name);
          
          // Check for free plan expiration from URL
          const urlParams = new URLSearchParams(window.location.search);
          const hasFreePlanExpired = urlParams.get('expired') === 'free';
          
          return (
            <motion.div key={plan.id} variants={item}>
              <Card 
                className={`flex flex-col h-full relative ${
                  isCurrentPlan || (isAdmin && isProPlan) || (isApproved && isProPlan)
                    ? 'border-2 border-secondary-500 bg-secondary-50'
                    : isTrial
                    ? 'border-2 border-accent-500 bg-accent-50'
                    : isFreePlan && hasFreePlanExpired
                    ? 'border-2 border-orange-500 bg-orange-50'
                    : isFreePlan
                    ? 'border-2 border-green-500 bg-green-50'
                    : 'border-primary-100'
                }`}
              >
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-left flex items-center justify-between">
                    <span>{plan.name === 'Starter' ? 'Free' : plan.name}</span>
                    <div className="flex flex-col gap-1">
                      {(isCurrentPlan || (isAdmin && isProPlan) || (isApproved && isProPlan)) && (
                        <span className="bg-secondary-500 text-white px-2 py-1 text-xs rounded-full">
                          {isAdmin ? 'Admin Access' : isApproved ? 'Approved' : 'Current Plan'}
                        </span>
                      )}
                      {isFreePlan && hasFreePlanExpired && (
                        <span className="bg-orange-500 text-white px-2 py-1 text-xs rounded-full">
                          Expired
                        </span>
                      )}
                    </div>
                  </CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                  
                  <div className="mt-4">
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold text-green-600">
                        {priceDetails.displayPrice}
                      </span>
                      {!isFreePlan && <span className="text-muted-foreground ml-1">/month</span>}
                    </div>
                    
                    {!isFreePlan && isAnnual && (
                      <div className="text-sm text-accent-500 mt-1">
                        Billed annually ({discount}% discount) • Includes 18% GST
                      </div>
                    )}
                    
                    {!isFreePlan && !isAnnual && (
                      <div className="text-sm text-muted-foreground mt-1">
                        Includes 18% GST
                      </div>
                    )}
                    
                    {isFreePlan && (
                      <div className="text-sm text-green-600 mt-1">
                        7-day access to basic features
                      </div>
                    )}
                    
                    {isTrial && (
                      <div className="text-sm text-accent-500 mt-1">
                        7-day full access trial • Includes 18% GST
                      </div>
                    )}
                  </div>
                </CardHeader>
                
                <CardContent className="flex-grow">
                  <ul className="space-y-2">
                    {features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-primary-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                    {isFreePlan && (
                      <li className="border-t pt-2 mt-2 text-green-700 text-sm">
                        <strong>Note:</strong> Free plan provides 7-day access to basic features. Upgrade anytime to continue access.
                      </li>
                    )}
                    {isTrial && (
                      <li className="border-t pt-2 mt-2 text-accent-700 text-sm">
                        <strong>Note:</strong> Trial is available only once per account. Monthly billing starts after trial period ends.
                      </li>
                    )}
                  </ul>
                </CardContent>
                
                <CardFooter>
                  {isAdmin || isApproved ? (
                    <Button 
                      variant="outline" 
                      className="w-full bg-green-50 border-green-300 hover:bg-green-100"
                      onClick={() => navigate('/dashboard')}
                    >
                      Access Dashboard
                    </Button>
                  ) : session?.user ? (
                    isCurrentPlan ? (
                      <Button 
                        variant="outline" 
                        className="w-full" 
                        onClick={() => navigate('/settings')}
                      >
                        {isFreePlan ? 'Upgrade Plan' : 'Manage Subscription'}
                      </Button>
                    ) : (
                      isFreePlan ? (
                        <Button
                          variant="outline"
                          className="w-full bg-green-500 text-white hover:bg-green-600"
                          onClick={() => handleStartFreePlan(session.user.id)}
                          disabled={hasActiveSubscription && !hasFreePlanExpired}
                        >
                          {hasFreePlanExpired ? 'Restart Free Plan' : (hasActiveSubscription ? 'Current Plan' : 'Start Free Plan')}
                        </Button>
                      ) : isPlanDowngrade ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="w-full">
                                <Button 
                                  variant="outline" 
                                  className="w-full cursor-not-allowed opacity-70"
                                  disabled={true}
                                >
                                  Cannot Downgrade
                                </Button>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p>You cannot downgrade from {currentPlanName} to {plan.name}. Wait until your current subscription expires.</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <CheckoutButton 
                          planId={plan.id}
                          billingCycle={isAnnual ? 'yearly' : 'monthly'} 
                          className={`w-full font-bold ${
                            isFreePlan 
                              ? 'bg-green-500 hover:bg-green-600 text-white' 
                              : isTrial 
                                ? 'bg-accent-500 hover:bg-accent-600 text-white' 
                                : ''
                          }`}
                        >
                          {isTrial ? `Start Trial (${priceDetails.displayPrice})` : 
                           `Subscribe (${priceDetails.displayPrice})`}
                        </CheckoutButton>
                      )
                    )
                  ) : (
                    <Button 
                      variant={isFreePlan ? "secondary" : isTrial ? "default" : "default"}
                      className={`w-full font-bold ${
                        isFreePlan ? 
                          'bg-green-500 hover:bg-green-600 text-white' : 
                          isTrial ? 
                            'bg-accent-500 hover:bg-accent-600 text-white' : ''
                      }`}
                      onClick={handleAuthRequired}
                    >
                      {isFreePlan ? 'Sign in for Free Plan' : 
                       isTrial ? `Sign in for Trial (${priceDetails.displayPrice})` : 
                       `Sign in to Subscribe (${priceDetails.displayPrice})`}
                    </Button>
                  )}
                </CardFooter>
              </Card>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
}
